<template>
  <div class="card">
    <a-collapse
        v-model:activeKey="mobileOpenKey"
        :bordered="false"
        class="faq-mobile faq-mobile__outer"
        expand-icon-position="end"
    >
      <a-collapse-panel key="menu" class="faq-mobile__panel">
        <template #header>
          <div class="faq-mobile__header">
            <div class="faq-mobile__title">{{ currentLabel }}</div>
            <UnorderedListOutlined
                v-if="!isMobileMenuOpen"
                class="faq-mobile__menu-icon"
            />
          </div>
        </template>

        <div class="faq-nav">
          <div v-for="item in faqNav()" :key="item.value" class="faq-nav__row">
            <a-collapse v-if="item.children" :bordered="false" class="faq-nav__collapse">
              <a-collapse-panel :key="item.value" :header="item.label" class="faq-nav__panel">
                <div
                    v-for="child in item.children"
                    :key="child.anchor"
                    class="faq-nav__child"
                    :class="{ 'is-active': activeAnchor === child.anchor }"
                    @click.prevent="onChildClick(item.value, child.anchor)"
                >
                  {{ child.label }}
                </div>
              </a-collapse-panel>
            </a-collapse>

            <div
                v-else
                class="faq-nav__item"
                :class="{ 'is-active': selected === item.value }"
                @click.prevent="onMainClick(item.value)"
            >
              {{ item.label }}
            </div>
          </div>
        </div>
      </a-collapse-panel>
    </a-collapse>
  </div>
</template>

<script>
import {faqNav} from "@/mixins/faqNav";
import {computed} from "@vue/reactivity";
import {ref} from "@vue/runtime-core";
import {nextTick} from "vue";
import {UnorderedListOutlined} from "@ant-design/icons-vue";

export default {
  components: {UnorderedListOutlined},
  methods: {
    faqNav() {
      return faqNav
    }
  },
  props: {
    modelValue: {
      type: String,
      default: "mainBlock",
    },
  },
  emits: ["update:modelValue", "change"],
  setup(props, context) {
    const selected = computed(() => props.modelValue);
    const activeAnchor = ref(null);
    const mobileOpenKey = ref([]);
    const isMobileMenuOpen = computed(() => mobileOpenKey.value?.includes("menu"));

    const selectBlock = (value) => {
      context.emit("update:modelValue", value);
      context.emit("change", value);
      activeAnchor.value = null;
    };

    const scrollToAnchor = (anchorId) => {
      const el = document.getElementById(anchorId);
      if (!el) return;
      el.scrollIntoView({ behavior: "smooth", block: "start" });
    };

    const onChildClick = async (blockKey, anchorId) => {
      if (blockKey && blockKey !== props.modelValue) {
        selectBlock(blockKey);
      }

      activeAnchor.value = anchorId;
      closeMobileMenu();

      await nextTick();
      window.setTimeout(() => scrollToAnchor(anchorId), 100);
    };


    const closeMobileMenu = () => {
      mobileOpenKey.value = [];
    };

    const onMainClick = (value) => {
      selectBlock(value);
      activeAnchor.value = null;
      closeMobileMenu();
    };

    const currentLabel = computed(() => {
      const main = faqNav.find((x) => x.value === props.modelValue);
      if (main) return main.label;
      return "Меню";
    });

    return {
      selected,
      selectBlock,
      scrollToAnchor,
      activeAnchor,
      onChildClick,
      currentLabel,
      onMainClick,
      mobileOpenKey,
      isMobileMenuOpen
    };
  }
}
</script>

<style scoped>
.card {
  margin-bottom: 0;
}

.faq-nav {
  display: block;
}

.faq-nav__item {
  display: block;
  padding: 10px 12px;
  color: var(--main-font);
  font-size: 14px;
  line-height: 20px;
  text-decoration: none;
  border-radius: 8px;
  cursor: pointer;
  font-family: "Open Sans", sans-serif;
}

.faq-nav__item.is-active {
  background: #f2f2f2;
  font-weight: 600;
}

.faq-nav__collapse :deep(.ant-collapse) {
  border: 0;
  background: #FFFFFF;
  font-family: "Open Sans", sans-serif;
}

.faq-nav__collapse :deep(.ant-collapse-item) {
  border: 0;
  background: #FFFFFF;
  font-family: "Open Sans", sans-serif;
}

.faq-nav__collapse :deep(.ant-collapse-header) {
  padding: 10px 12px !important;
  background: #FFFFFF !important;
  color: var(--main-font) !important;
  font-size: 14px !important;
  line-height: 20px !important;

  display: flex !important;
  align-items: center !important;
  gap: 8px !important;
}

.faq-nav__collapse :deep(.ant-collapse-header) {
  padding-inline-start: 12px !important;
  font-family: "Open Sans", sans-serif;
}

.faq-nav__collapse :deep(.ant-collapse-header-text) {
  flex: 1 1 auto !important;
}

.faq-nav__collapse :deep(.ant-collapse-expand-icon) {
  order: 2 !important;
  margin-inline-start: auto !important;
  margin-inline-end: 0 !important;
  color: var(--main-font) !important;
  font-size: 12px;
}

.faq-nav__collapse :deep(.ant-collapse-header .ant-collapse-expand-icon) {
  padding: 0 !important;
}

.faq-nav__collapse :deep(.ant-collapse-item-active > .ant-collapse-header) {
  background: #FFFFFF !important;
}

.faq-nav__collapse :deep(.ant-collapse-header:hover) {
  background: #f7f7f7 !important;
  border-radius: 8px;
}

.faq-nav__collapse :deep(.ant-collapse-content) {
  margin-top: 8px !important;
  background: #FFFFFF !important;
  border: 0 !important;
  padding: 0 !important;
}

:deep(.ant-collapse-content-box) {
  padding: 0 !important;
  margin-top: 8px;
}

.faq-nav__children {
  padding: 4px 0 10px 24px;
}

.faq-nav__child {
  font-size: 12px;
  color: var(--grey-text);
  border-radius: 8px;
  display: block;
  padding: 8px 12px 8px 22px;
  line-height: 20px;
  text-decoration: none;
  cursor: pointer;
}

.faq-nav__child.is-active {
  background: #f2f2f2;
  border-radius: 8px;
  color: var(--main-font);
  font-weight: 600;
}

.faq-nav__item:hover {
  background: #f7f7f7;
}

.faq-nav__child:hover {
  color: #595959;
}

.faq-mobile :deep(.ant-collapse) {
  border: 0;
  background: #FFFFFF;
}

.faq-mobile :deep(.ant-collapse-item) {
  border: 0;
  background: #FFFFFF;
}

.faq-mobile :deep(.ant-collapse-header) {
  padding: 0 !important;
  background: #FFFFFF !important;
}

.faq-mobile__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
}

.faq-mobile__title {
  color: var(--main-font);
  font-size: 16px;
  font-weight: 600;
}

.faq-mobile__icon {
  font-size: 18px;
  color: var(--main-font);
}

.faq-mobile__outer :deep(> .ant-collapse-item:not(.ant-collapse-item-active) > .ant-collapse-header .ant-collapse-expand-icon),
.faq-mobile__outer :deep(> .ant-collapse-item:not(.ant-collapse-item-active) > .ant-collapse-header .ant-collapse-arrow) {
  display: none !important;
}

.faq-nav__collapse :deep(.ant-collapse) {
  border: 0 !important;
  background: transparent !important;
}

.faq-nav__collapse :deep(.ant-collapse-item) {
  border: 0 !important;
  background: transparent !important;
}

.faq-nav__collapse :deep(.ant-collapse-header) {
  padding: 10px 12px !important;
  background: transparent !important;
  color: var(--main-font) !important;
  font-size: 14px !important;
  line-height: 20px !important;

  display: flex !important;
  align-items: center !important;
  border-radius: 8px;
}

.faq-nav__collapse :deep(.ant-collapse-header:hover) {
  background: #f7f7f7 !important;
}

.faq-nav__collapse :deep(.ant-collapse-header-text) {
  flex: 1 1 auto !important;
}

.faq-nav__collapse :deep(.ant-collapse-expand-icon),
.faq-nav__collapse :deep(.ant-collapse-arrow) {
  margin-left: auto !important;
  color: var(--main-font) !important;
}

.faq-nav__collapse :deep(.ant-collapse-content) {
  margin-top: 0 !important;
  background: transparent !important;
  border: 0 !important;
}

.faq-nav__collapse :deep(.ant-collapse-content-box) {
  padding: 0 !important;
}
</style>